/**
 * Leitura dos CSVs do INPE (Programa Queimadas). Funções puras, usadas na
 * thread principal (10 min) e no Web Worker (diário).
 *
 * Baseado no parser robusto de `helvecioneto/olharCidadao`
 * (supabase/functions/_shared/inpe.ts): lat/lon alinhados com espaços, BOM,
 * CRLF, linhas vazias, campos entre aspas e `-999` = sem dado.
 */
import type { DailyColumns, FireTable, RawFire10 } from './types';

/** Valor usado pelo INPE para "sem dado" nas colunas numéricas. */
export const NO_DATA = -999;

// ---------------------------------------------------------------------------
// CSV genérico
// ---------------------------------------------------------------------------

/**
 * Percorre os registros de um CSV e devolve o texto de cada um, sem o
 * terminador. Trata BOM, CRLF/LF (e CR isolado), linhas vazias e quebras de
 * linha dentro de aspas.
 */
export function* iterateCsvRecords(text: string): Generator<string> {
  let src = text;
  if (src.indexOf('\n') === -1 && src.indexOf('\r') !== -1) src = src.replace(/\r/g, '\n');
  const size = src.length;
  let start = src.charCodeAt(0) === 0xfeff ? 1 : 0;
  while (start < size) {
    let end = src.indexOf('\n', start);
    if (end === -1) end = size;
    let record = src.slice(start, end);
    while (end < size && record.indexOf('"') !== -1 && countQuotes(record) % 2 === 1) {
      // Aspas abertas: a quebra de linha pertence ao campo.
      let next = src.indexOf('\n', end + 1);
      if (next === -1) next = size;
      record += '\n' + src.slice(end + 1, next);
      end = next;
    }
    start = end + 1;
    if (record.endsWith('\r')) record = record.slice(0, -1);
    if (record.trim() === '') continue;
    yield record;
  }
}

function countQuotes(s: string): number {
  let n = 0;
  for (let i = s.indexOf('"'); i !== -1; i = s.indexOf('"', i + 1)) n++;
  return n;
}

/**
 * Separa um registro em campos. Campos sem aspas perdem os espaços ao redor
 * (o INPE alinha lat/lon com espaços); campos entre aspas preservam o conteúdo.
 */
export function splitCsvRecord(record: string): string[] {
  if (record.indexOf('"') === -1) return record.split(',').map((f) => f.trim());
  const fields: string[] = [];
  let field = '';
  let inQuotes = false;
  let quoted = false;
  for (let i = 0; i < record.length; i++) {
    const c = record[i] as string;
    if (inQuotes) {
      if (c !== '"') field += c;
      else if (record[i + 1] === '"') {
        field += '"';
        i++;
      } else inQuotes = false;
    } else if (c === ',') {
      fields.push(quoted ? field : field.trim());
      field = '';
      quoted = false;
    } else if (c === '"' && !quoted && field.trim() === '') {
      inQuotes = true;
      quoted = true;
      field = '';
    } else if (!quoted || c.trim() !== '') {
      field += c;
    }
  }
  fields.push(quoted ? field : field.trim());
  return fields;
}

/** Mapeia nome de coluna → índice (ignora caixa, espaços e BOM). */
export function mapHeader(fields: string[]): Map<string, number> {
  const map = new Map<string, number>();
  fields.forEach((name, i) => {
    const key = name.replace(/^﻿/, '').trim().toLowerCase();
    if (key && !map.has(key)) map.set(key, i);
  });
  return map;
}

// ---------------------------------------------------------------------------
// Conversões
// ---------------------------------------------------------------------------

/** Número ou null (vazio, inválido ou sentinela −999). */
export function toNumber(value: string | undefined): number | null {
  if (value === undefined) return null;
  const s = value.trim();
  if (s === '') return null;
  const n = Number(s);
  if (!Number.isFinite(n) || n <= NO_DATA) return null;
  return n;
}

export function toText(value: string | undefined): string | null {
  const s = (value ?? '').trim();
  return s === '' ? null : s;
}

/** Código IBGE de município (7 dígitos) como número; aceita `1500404.0` e espaços. */
export function toIbgeCode(value: string | undefined): number | null {
  if (value === undefined) return null;
  const n = Number(value.trim());
  if (!Number.isInteger(n) || n < 1_000_000 || n > 9_999_999) return null;
  return n;
}

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?$/;

/**
 * Data/hora do INPE (GMT) → epoch ms. Aceita `AAAA-MM-DD HH:MM:SS`,
 * `AAAA-MM-DD HH:MM`, ISO 8601 e **só a data** (`AAAA-MM-DD` = 00:00 UTC —
 * os arquivos de 10 min usam esse formato quando todas as detecções são da
 * meia-noite). Devolve null se inválida.
 */
export function parseUtcDateTime(value: string | undefined): number | null {
  if (value === undefined) return null;
  const m = DATE_RE.exec(value.trim());
  if (!m) return null;
  const [, y, mo, d, h = '00', mi = '00', s = '00', tz] = m;
  if (tz && tz !== 'Z') {
    const t = Date.parse(`${y}-${mo}-${d}T${h}:${mi}:${s}${tz.length === 5 ? tz.slice(0, 3) + ':' + tz.slice(3) : tz}`);
    return Number.isNaN(t) ? null : t;
  }
  const t = Date.UTC(+y!, +mo! - 1, +d!, +h, +mi, +s);
  const check = new Date(t);
  if (check.getUTCDate() !== +d! || check.getUTCMonth() !== +mo! - 1 || +h > 23 || +mi > 59 || +s > 59) return null;
  return t;
}

function validCoord(lat: number, lon: number): boolean {
  return lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180;
}

// ---------------------------------------------------------------------------
// Arquivo de 10 min: lat,lon,satelite,data
// ---------------------------------------------------------------------------

export interface Parse10minResult {
  fires: RawFire10[];
  /** Linhas de dados lidas (sem cabeçalho). */
  read: number;
  /** Linhas descartadas por lat/lon/data/satélite inválidos. */
  invalid: number;
}

/**
 * Lê um CSV de 10 min. Com `satellite`, mantém só esse satélite (os arquivos
 * de 10 min também trazem, com 2–3 h de atraso, detecções de satélites
 * polares, que já vêm no arquivo diário).
 */
export function parse10minCsv(text: string, opts: { satellite?: string } = {}): Parse10minResult {
  const records = iterateCsvRecords(text);
  const first = records.next();
  if (first.done) return { fires: [], read: 0, invalid: 0 };
  const header = mapHeader(splitCsvRecord(first.value));
  const iLat = header.get('lat');
  const iLon = header.get('lon');
  const iSat = header.get('satelite');
  const iData = header.get('data') ?? header.get('data_hora_gmt');
  if (iLat === undefined || iLon === undefined || iSat === undefined || iData === undefined) {
    throw new Error('CSV de 10 min sem as colunas lat, lon, satelite, data');
  }
  const want = opts.satellite?.toUpperCase();
  const fires: RawFire10[] = [];
  let read = 0;
  let invalid = 0;
  for (const record of records) {
    read++;
    const f = splitCsvRecord(record);
    const satelite = toText(f[iSat]);
    const lat = toNumber(f[iLat]);
    const lon = toNumber(f[iLon]);
    const t = parseUtcDateTime(f[iData]);
    if (!satelite || lat === null || lon === null || t === null || !validCoord(lat, lon)) {
      invalid++;
      continue;
    }
    if (want && satelite.toUpperCase() !== want) continue;
    fires.push({ lat, lon, t, satelite });
  }
  return { fires, read, invalid };
}

/** Chave de deduplicação: `lat|lon|data` (4 casas decimais, como publicado). */
export function fireKey(lat: number, lon: number, t: number): string {
  return `${lat.toFixed(4)}|${lon.toFixed(4)}|${t}`;
}

/** Junta focos de vários arquivos, sem repetir `lat|lon|data`, ordenados por data. */
export function mergeDedup10min(lists: Iterable<RawFire10[]>): RawFire10[] {
  const seen = new Set<string>();
  const out: RawFire10[] = [];
  for (const list of lists) {
    for (const f of list) {
      const k = fireKey(f.lat, f.lon, f.t);
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(f);
    }
  }
  out.sort((a, b) => a.t - b.t);
  return out;
}

// ---------------------------------------------------------------------------
// Arquivo diário (16 colunas)
// ---------------------------------------------------------------------------

/** Decide se um foco do diário está na Amazônia Legal. */
export type DailyAmazoniaTest = (cdMun: number | null, ufCode: number | null, lon: number, lat: number) => boolean;

export interface ParseDailyResult {
  table: FireTable;
  read: number;
  invalid: number;
}

class Dict {
  readonly names: string[] = [''];
  private readonly index = new Map<string, number>();
  get(name: string | null): number {
    if (!name) return 0;
    let i = this.index.get(name);
    if (i === undefined) {
      i = this.names.length;
      this.names.push(name);
      this.index.set(name, i);
    }
    return i;
  }
}

const numOrNaN = (v: string | undefined): number => toNumber(v) ?? NaN;

/**
 * Lê o CSV diário em uma tabela colunar ordenada por data. Mantém todas as
 * linhas válidas e marca em `amz` as da Amazônia Legal (o filtro "Somente
 * Amazônia Legal" pode então ser ligado/desligado sem novo download).
 */
export function parseDailyCsv(text: string, isAmazonia: DailyAmazoniaTest): ParseDailyResult {
  const records = iterateCsvRecords(text);
  const first = records.next();
  if (first.done) return { table: emptyTable('diario', true), read: 0, invalid: 0 };
  const h = mapHeader(splitCsvRecord(first.value));
  const need = ['lat', 'lon', 'data_hora_gmt', 'satelite'];
  const missing = need.filter((c) => !h.has(c));
  if (missing.length) throw new Error(`CSV diário sem as colunas: ${missing.join(', ')}`);
  const col = (f: string[], name: string) => {
    const i = h.get(name);
    return i === undefined ? undefined : f[i];
  };

  const lat: number[] = [];
  const lon: number[] = [];
  const t: number[] = [];
  const sat: number[] = [];
  const amz: number[] = [];
  const ids: string[] = [];
  const mun: number[] = [];
  const uf: number[] = [];
  const cdMun: number[] = [];
  const dias: number[] = [];
  const precip: number[] = [];
  const risco: number[] = [];
  const bioma: number[] = [];
  const frp: number[] = [];
  const satDict = new Dict();
  const munDict = new Dict();
  const biomaDict = new Dict();
  let read = 0;
  let invalid = 0;

  for (const record of records) {
    read++;
    const f = splitCsvRecord(record);
    const la = toNumber(col(f, 'lat'));
    const lo = toNumber(col(f, 'lon'));
    const ti = parseUtcDateTime(col(f, 'data_hora_gmt'));
    const sa = toText(col(f, 'satelite'));
    if (la === null || lo === null || ti === null || !sa || !validCoord(la, lo)) {
      invalid++;
      continue;
    }
    const cd = toIbgeCode(col(f, 'municipio_id'));
    let ufc = toNumber(col(f, 'estado_id'));
    if (ufc === null && cd !== null) ufc = Math.floor(cd / 100_000);
    lat.push(la);
    lon.push(lo);
    t.push(ti);
    sat.push(satDict.get(sa) - 1);
    amz.push(isAmazonia(cd, ufc, lo, la) ? 1 : 0);
    ids.push(toText(col(f, 'id')) ?? `${la}|${lo}|${ti}`);
    mun.push(munDict.get(toText(col(f, 'municipio'))));
    uf.push(ufc !== null && ufc > 0 && ufc < 256 ? ufc : 0);
    cdMun.push(cd ?? 0);
    dias.push(numOrNaN(col(f, 'numero_dias_sem_chuva')));
    precip.push(numOrNaN(col(f, 'precipitacao')));
    risco.push(numOrNaN(col(f, 'risco_fogo')));
    bioma.push(biomaDict.get(toText(col(f, 'bioma'))));
    frp.push(numOrNaN(col(f, 'frp')));
  }

  // Ordena por data (o INPE publica agrupado por satélite/passagem).
  const n = t.length;
  const order = new Uint32Array(n);
  for (let i = 0; i < n; i++) order[i] = i;
  order.sort((a, b) => t[a]! - t[b]! || a - b);

  const table = emptyTable('diario', true, n);
  const ex = table.extra!;
  for (let k = 0; k < n; k++) {
    const i = order[k]!;
    table.lat[k] = lat[i]!;
    table.lon[k] = lon[i]!;
    table.t[k] = t[i]!;
    table.sat[k] = sat[i]!;
    table.amz[k] = amz[i]!;
    ex.ids[k] = ids[i]!;
    ex.mun[k] = mun[i]!;
    ex.uf[k] = uf[i]!;
    ex.cdMun[k] = cdMun[i]!;
    ex.dias[k] = dias[i]!;
    ex.precip[k] = precip[i]!;
    ex.risco[k] = risco[i]!;
    ex.bioma[k] = bioma[i]!;
    ex.frp[k] = frp[i]!;
  }
  table.dict.sat = satDict.names.slice(1);
  ex.munNames = munDict.names;
  ex.biomaNames = biomaDict.names;
  return { table, read, invalid };
}

/** Cria uma tabela vazia (ou com `n` linhas zeradas). */
export function emptyTable(source: FireTable['source'], daily: boolean, n = 0): FireTable {
  const table: FireTable = {
    source,
    length: n,
    lat: new Float64Array(n),
    lon: new Float64Array(n),
    t: new Float64Array(n),
    x: new Float64Array(n).fill(NaN),
    y: new Float64Array(n).fill(NaN),
    sat: new Uint8Array(n),
    amz: new Uint8Array(n),
    dict: { sat: [] },
  };
  if (daily) {
    const extra: DailyColumns = {
      ids: new Array<string>(n),
      mun: new Uint16Array(n),
      munNames: [''],
      uf: new Uint8Array(n),
      cdMun: new Int32Array(n),
      dias: new Float32Array(n),
      precip: new Float32Array(n),
      risco: new Float32Array(n),
      bioma: new Uint8Array(n),
      biomaNames: [''],
      frp: new Float32Array(n),
    };
    table.extra = extra;
  }
  return table;
}

/** Buffers transferíveis de uma tabela (para `postMessage`). */
export function tableTransferables(table: FireTable): ArrayBuffer[] {
  const arrays: ArrayBufferView[] = [table.lat, table.lon, table.t, table.x, table.y, table.sat, table.amz];
  const e = table.extra;
  if (e) arrays.push(e.mun, e.uf, e.cdMun, e.dias, e.precip, e.risco, e.bioma, e.frp);
  return arrays.map((a) => a.buffer as ArrayBuffer);
}
